// @spec AST-STR-001
// @spec AST-STR-003
// @spec AST-STR-006
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, Settings2, Upload } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { useTranslation } from 'react-i18next';

import { RiskSecurityModuleContext } from '@/components/risk-security-module-context';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AssetFilters } from '@/features/assets/components/AssetFilters';
import { AssetsTable } from '@/features/assets/components/AssetsTable';
import { CreateAssetDialog } from '@/features/assets/components/CreateAssetDialog';
import { ExportAssetsButton } from '@/features/assets/components/ExportAssetsButton';
import { useAssets } from '@/features/assets/hooks/useAssets';
import type { AssetFilters as AssetFiltersState } from '@/features/assets/types';
import { useAssetGovernanceProfile } from '@/features/settings/assets/api/use-asset-governance-profile';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

// URL search params schema for validation
const assetListSearchSchema = z.object({
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(20),
  category: z
    .string()
    .optional(),
  status: z
    .union([
      z.array(z.enum(['draft', 'active', 'under_review', 'deprecated', 'decommissioned'])),
      z.enum(['draft', 'active', 'under_review', 'deprecated', 'decommissioned']),
    ])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  criticality: z
    .union([
      z.array(z.enum(['critical', 'high', 'medium', 'low'])),
      z.enum(['critical', 'high', 'medium', 'low']),
    ])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  tags: z
    .union([z.array(z.string()), z.string()])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  search: z.string().optional(),
  sortBy: z
    .enum(['name', 'created_at', 'updated_at', 'criticality', 'status'])
    .optional()
    .default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export const Route = createFileRoute('/_authenticated/assets/')({
  validateSearch: assetListSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.assets'),
  }),
  component: AssetsListPage,
});

/**
 * Assets list page - main entry point for asset management.
 */
function AssetsListPage() {
  const { t } = useTranslation('assets');
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/assets/' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Extract filter state from URL search params
  const filters: AssetFiltersState = React.useMemo(
    () => ({
      category: search.category ?? null,
      status: search.status ?? null,
      criticality: search.criticality ?? null,
      tags: search.tags ?? null,
      search: search.search ?? null,
      sortBy: search.sortBy,
      sortOrder: search.sortOrder,
    }),
    [search]
  );

  // Fetch assets with current filters
  const {
    data: assetsData,
    isLoading: assetsLoading,
    isFetching: assetsFetching,
    isError: assetsError,
    error: assetsQueryError,
    refetch: refetchAssets,
  } = useAssets({
    orgId: activeOrg?.id ?? '',
    page: search.page,
    pageSize: search.pageSize,
    ...filters,
    enabled: Boolean(activeOrg),
  });

  const { data: governanceProfile } = useAssetGovernanceProfile({
    ...(activeOrg?.id ? { orgId: activeOrg.id } : {}),
    enabled: Boolean(activeOrg?.id),
  });

  // Update filters in URL
  const handleFiltersChange = React.useCallback(
    (newFilters: AssetFiltersState) => {
      // Convert null to undefined to avoid Zod validation errors
      const searchUpdate = {
        ...search,
        category: newFilters.category ?? undefined,
        status: newFilters.status ?? undefined,
        criticality: newFilters.criticality ?? undefined,
        tags: newFilters.tags ?? undefined,
        search: newFilters.search ?? undefined,
        sortBy: newFilters.sortBy,
        sortOrder: newFilters.sortOrder,
        page: 1, // Reset to first page on filter change
      };
      void navigate({
        search: searchUpdate as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle pagination
  const handlePageChange = React.useCallback(
    (page: number) => {
      void navigate({
        search: { ...search, page } as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle asset click - navigate to detail
  const handleAssetClick = React.useCallback(
    (assetId: string) => {
      void navigate({
        to: '/assets/$assetId',
        params: { assetId },
      });
    },
    [navigate]
  );

  // Handle clear filters
  const handleClearFilters = React.useCallback(() => {
    handleFiltersChange({
      category: null,
      status: null,
      criticality: null,
      tags: null,
      search: null,
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
  }, [handleFiltersChange]);

  // Handle create asset
  const handleCreateAsset = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  // Handle successful asset creation
  const handleCreateSuccess = React.useCallback(
    (asset: { id: string }) => {
      void navigate({
        to: '/assets/$assetId',
        params: { assetId: asset.id },
      });
    },
    [navigate]
  );

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('listPage.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('listPage.selectOrgToManage')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('listPage.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasFilters = Boolean(
    filters.category ?? filters.status ?? filters.criticality ?? filters.tags ?? filters.search
  );
  const pagination = assetsData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.page ?? search.page;
  const totalItems = pagination?.totalItems ?? assetsData?.items.length ?? 0;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('listPage.title')}</h1>
            <p className="mt-1 text-muted-foreground">{t('listPage.subtitle', { orgName: activeOrg.name })}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                void navigate({
                  to: '/settings/configration',
                  search: { aspect: 'assets', section: 'attributes' },
                })
              }
            >
              <Settings2 className="mr-2 h-4 w-4" />
              {t('listPage.configAndGovernance')}
            </Button>
            <ExportAssetsButton filters={filters} />
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/assets/import', search: { uploadId: undefined } })}
            >
              <Upload className="mr-2 h-4 w-4" />
              {t('listPage.import')}
            </Button>
            <Button onClick={handleCreateAsset}>
              <Plus className="mr-2 h-4 w-4" />
              {t('listPage.createAsset')}
            </Button>
          </div>
        </header>

        <RiskSecurityModuleContext module="asset-inventory" />

        {/* Create Asset Dialog */}
        <CreateAssetDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSuccess={handleCreateSuccess}
          orgId={activeOrg.id}
        />

        {/* Filter Bar */}
        <AssetFilters filters={filters} onChange={handleFiltersChange} orgId={activeOrg.id} />

        {governanceProfile?.status === 'published' ? (
          <Alert>
            <AlertDescription>
              {t('listPage.guidelinesActive', {
                version: String(governanceProfile.guidelinesVersion),
                updatedAt: new Date(governanceProfile.updatedAt).toLocaleString(),
              })}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <AlertDescription>
              {t('listPage.noGuidelinesActive')}
            </AlertDescription>
          </Alert>
        )}

        {assetsError && (
          <Alert variant="destructive">
            <AlertDescription>
              {t('listPage.failedToLoadAssets', {
                error: (assetsQueryError as Error | null)?.message ?? t('listPage.unknownError'),
              })}
              <Button
                variant="outline"
                size="sm"
                className="ml-3"
                onClick={() => {
                  void refetchAssets();
                }}
              >
                {t('listPage.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <AssetsTable
          assets={assetsData?.items ?? []}
          isLoading={assetsLoading || assetsFetching}
          total={totalItems}
          page={currentPage}
          pageSize={search.pageSize}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onNavigate={handleAssetClick}
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
          onCreateAsset={handleCreateAsset}
        />
      </div>
    </div>
  );
}
